import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ColumnActionWithMenuV2,
  ColumnMenuOption,
  PlanType,
  StatusChipV3,
  TableComponentV2,
} from '@labrav/react-components'
import { isEmpty } from 'lodash/fp'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ShortenedNameV2 } from '@labrav/common'
import { ExtendOfferExpirationDateModal } from '../ExtendOfferExpirationDateModal'
import { ShowExpirationDateWithIcon } from '../../common/components/ShowExpirationDateWithIcon/ShowExpirationDateWithIcon'
import {
  useTenantPartnerId,
  useUserType,
  usePartner,
  useScreenSize,
  useSyncCloudMarketplace,
  useValidateCloudRoute,
  providerToAuthCloud,
  LoadingTypes,
  isLoading,
  useChatContext,
  useCurrentCloudProvider,
  type AccountDataSelection,
} from '@labrav/common'
import { getStatusAndLabelForChannelOffer } from '../../common/utils/getStatusAndLabelForChannelOffer'
import { useDispatch, useSelector } from '../../store'
import {
  clearChannelOffers,
  getChannelPartnerPrivateOffers,
  updateChannelPartnerPrivateOffer,
  deleteCurrentCPPO,
} from '../../modules/channelPartnerPrivateOffer/action'
import type {
  ChannelPartnerPrivateOffer,
  ChannelPartnerPrivateOffersState,
} from '../../modules/channelPartnerPrivateOffer/reducer'
import { OfferStatus, OfferTypeRenew } from '../../modules/privateOffer/reducer'
import { useStyles } from './ChannelOffers.styles'
import { ChannelOffersFilters } from './ChannelOffersFilters/ChannelOffersFilters'
import {
  getActiveReceivedResaleAuthorizations,
  syncReceivedResaleAuthorizations,
} from '../../modules/receivedResellerAuthorization/action'
import type { ReceivedResellerAuthorizationState } from '../../modules/receivedResellerAuthorization/reducer'
import { useCommonStyles } from '../../common/styles/common.styles'
import { ResponsiveColumn } from '../../common/components/ResponsiveColumn/ResponsiveColumn'
import { useSyncFlyoutRouteParams } from '../../common/utils/hooks/useSyncFlyoutRouteParams'
import type { CloudMarketplace } from '../../modules/productsListing/types'
import { useAuth } from '@labrav/react-components'
import { ModalConfirmV2, ModalSizeVariants } from '@labrav/react-components'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'
import { Typography } from '@mui/material'
import { IsvChannelOffersEmptyState } from './IsvChannelOffersEmptyState/IsvChannelOffersEmptyState'
import {
  CreateChannelOfferEmptyState,
  SyncResaleAuthEmptyState,
} from './ChannelOffersEmptyStates'
import { mappingChannelOfferForLabraUI } from './constants'
import {
  getCppoMenuOptions,
  getLabraResaleAuthDetailPageUrl,
  getOfferTypeWithToolTip,
  getStatusOffer,
} from './helpers'
import type { ChannelOfferProps } from './types'

export { mappingChannelOfferForLabraUI } from './constants'
export { getStatusOffer } from './helpers'
export type { ChannelOfferProps } from './types'

export const ChannelOffers: React.FC<ChannelOfferProps> = ({
  externalBaseURL,
}) => {
  useSyncFlyoutRouteParams(!!externalBaseURL)
  useSyncCloudMarketplace()
  useValidateCloudRoute('flyout', '/flyout', !!externalBaseURL)

  const { classes } = useStyles()
  const { classes: commonClasses } = useCommonStyles()
  const { cloud: routeCloud, accountId: routeAccountId } = useParams<{
    cloud?: string
    accountId?: string
  }>()

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const cloudMarketplace = useCurrentCloudProvider()
  const { screenWidth, pageSize } = useScreenSize()
  const columnMaxWidth = useMemo(
    () => `${(screenWidth * 20) / 100}px`,
    [screenWidth]
  )

  const baseUrl = useMemo(() => {
    if (externalBaseURL) return externalBaseURL
    if (routeCloud && routeAccountId) {
      return `/flyout/${routeCloud}/${routeAccountId}`
    }
    return '/flyout'
  }, [externalBaseURL, routeCloud, routeAccountId])
  const [copied, setCopied] = useState<boolean>(false)
  const [openExpiryDateModalCPPO, setExpiryDateModalCPPO] = useState<string>('')
  const [shouldDisplayWaitTime, setDisplayWaitTime] = useState<boolean>(false)

  const getCpposProps = {
    partnerId: routeAccountId || '',
    pageSize: pageSize || 15,
  }

  const { awsCppos, pageNumber, count } =
    useSelector<ChannelPartnerPrivateOffersState>(
      state => state.channelPartnerPrivateOffers || {}
    )
  useChatContext('channelOffers', () => ({
    cloud: cloudMarketplace,
    visible: { count: count ?? 0, page: pageNumber ?? 1 },
  }))

  const allAccounts = useSelector<AccountDataSelection[]>(
    state =>
      (state.accountSelection?.availableAccounts ||
        []) as AccountDataSelection[]
  )

  useEffect(() => {
    if (
      !externalBaseURL &&
      routeCloud &&
      routeCloud.toUpperCase() !== 'AWS' &&
      routeAccountId
    ) {
      const flyoutAccounts = allAccounts.filter(
        acc => acc.product?.toLowerCase() === 'flyout'
      )

      let awsAccountId = flyoutAccounts.find(
        acc => acc.id === routeAccountId && acc.cloud === 'AWS'
      )?.id

      if (!awsAccountId) {
        awsAccountId = flyoutAccounts.find(acc => acc.cloud === 'AWS')?.id
      }

      if (awsAccountId) {
        navigate(
          `/flyout/aws/${awsAccountId}/offers-authorizations/channel-offers`,
          { replace: true }
        )
      }
    }
  }, [routeCloud, routeAccountId, externalBaseURL, navigate, allAccounts])

  useEffect(() => {
    const fetchActive = async () => {
      try {
        if (awsCppos) {
          await dispatch(
            getActiveReceivedResaleAuthorizations({
              partnerId: routeAccountId || '',
            })
          )
        }
      } catch (err) {
        console.error('getActiveReceivedResaleAuthorizations failed', err)
      }
    }
    fetchActive()
  }, [awsCppos, routeAccountId, dispatch])

  const { activeReceivedResellerAuthorizations } =
    useSelector<ReceivedResellerAuthorizationState>(
      state => state.receivedResellerAuthorization || {}
    )

  const selectedCppoDetail = (awsCppos || []).find(
    awsCppo => awsCppo.id === openExpiryDateModalCPPO
  )

  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.CHANNEL_OFFER_LOADING)
  )

  const isLoadingSync = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.SYNC_RECEIVED_RESALE_AUTHS_FROM_CLOUD)
  )

  const renderSyncActiveResaleAuthEmptyState =
    !loading &&
    isEmpty(awsCppos) &&
    isEmpty(activeReceivedResellerAuthorizations)

  const selectedActiveReceivedResellerAuthorization = useSelector<string>(
    state =>
      state?.receivedResellerAuthorization
        ?.selectedActiveReceivedResellerAuthorization
  )
  const renderCreateCPPOEmptyState =
    !loading &&
    activeReceivedResellerAuthorizations &&
    isEmpty(awsCppos) &&
    isEmpty(selectedActiveReceivedResellerAuthorization)

  useEffect(() => {
    let mounted = true
    const fetchOffers = async () => {
      try {
        await dispatch(
          getChannelPartnerPrivateOffers({
            ...getCpposProps,
            selectedResellerAuth: selectedActiveReceivedResellerAuthorization,
          })
        )
      } catch (err) {
        console.error('getChannelPartnerPrivateOffers failed', err)
      }
    }
    fetchOffers()
    return () => {
      try {
        if (mounted) dispatch(clearChannelOffers())
      } catch (err) {
        console.error('clearChannelOffers failed', err)
      }
      mounted = false
    }
  }, [routeAccountId, selectedActiveReceivedResellerAuthorization, dispatch])

  const fetchMoreData = useCallback(() => {
    if (awsCppos && awsCppos.length > 0) {
      try {
        dispatch(
          getChannelPartnerPrivateOffers({
            partnerId: routeAccountId || '',
            pageSize: pageSize,
            pageNumber: pageNumber + 1,
            selectedResellerAuth: selectedActiveReceivedResellerAuthorization,
          })
        )
      } catch (err) {
        console.error(
          'fetchMoreData: getChannelPartnerPrivateOffers failed',
          err
        )
      }
    }
  }, [
    routeAccountId,
    pageNumber,
    selectedActiveReceivedResellerAuthorization,
    dispatch,
    awsCppos,
    pageSize,
  ])

  const onAcceptModalAction = useCallback(
    async (data: Record<string, unknown>) => {
      try {
        await dispatch(
          updateChannelPartnerPrivateOffer(
            openExpiryDateModalCPPO,
            data,
            undefined,
            false,
            () => void 0,
            selectedCppoDetail?.resaleAuthorizationId,
            routeAccountId,
            (routeCloud as CloudMarketplace) || cloudMarketplace
          )
        )
        const navigateUrl = `${baseUrl}/authorization/${selectedCppoDetail?.resaleAuthorizationId}/channel-offers/${openExpiryDateModalCPPO}/detail`
        setExpiryDateModalCPPO('')
        navigate(navigateUrl)
      } catch (err) {
        console.error('onAcceptModalAction failed', err)
        setExpiryDateModalCPPO('')
      }
    },
    [
      openExpiryDateModalCPPO,
      baseUrl,
      selectedCppoDetail,
      routeAccountId,
      routeCloud,
      cloudMarketplace,
    ]
  )

  const onDeclineModalAction = async () => {
    setExpiryDateModalCPPO('')
  }

  const cloud = useCurrentCloudProvider()
  const { loading: authLoading, canDo } = useAuth()
  const canWriteMarketplace = useMemo(() => {
    return (
      !authLoading &&
      canDo({
        cloud: providerToAuthCloud(cloud.toLowerCase()),
        service: 'marketplace',
        action: 'create',
      })
    )
  }, [authLoading, canDo, cloud])

  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [selectedDeleteCppo, setSelectedDeleteCppo] =
    useState<ChannelPartnerPrivateOffer | null>(null)

  const handleOpenDeleteModal = (cppo: ChannelPartnerPrivateOffer) => {
    setSelectedDeleteCppo(cppo)
    setOpenDeleteModal(true)
  }
  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
    setSelectedDeleteCppo(null)
  }

  const deleteLoading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.DELETE_OFFER)
  )
  const handleConfirmDeleteModal = async () => {
    if (!selectedDeleteCppo) return
    await dispatch(
      deleteCurrentCPPO(
        selectedDeleteCppo.id,
        undefined,
        routeAccountId,
        cloudMarketplace
      )
    )
    handleCloseDeleteModal()
  }

  const handleSelectOption =
    (cppo: ChannelPartnerPrivateOffer) => async (option: ColumnMenuOption) => {
      switch (option.key) {
        case 'view-cppo-details': {
          if (option.route) {
            navigate(option?.route)
          }
          break
        }
        case 'copy-channel-offer-url': {
          if (navigator.clipboard && cppo.cloudOfferUrl) {
            navigator.clipboard
              .writeText(cppo.cloudOfferUrl)
              .then(() => {
                setCopied(true)
                setTimeout(() => {
                  setCopied(false)
                }, 2000)
              })
              .catch(error => {
                console.error('Error copying URL to clipboard:', error)
              })
          } else {
            console.warn('Clipboard API not available or URL is empty')
          }

          break
        }
        case 'extend-offer-expiration-date': {
          setExpiryDateModalCPPO(cppo?.id || '')
          break
        }
        case 'Delete-offer': {
          handleOpenDeleteModal(cppo)
          break
        }
        default: {
          ;() => null
        }
      }
    }

  const getCppoListObjectsForLabraUI = () => {
    return [
      ...(awsCppos || []).map(cppo => {
        const offerStatus = getStatusOffer(cppo)
        const statusDetails = getStatusAndLabelForChannelOffer(
          offerStatus || OfferStatus.PRIVATE_OFFER_CREATION_IN_PROGRESS
        )
        return {
          id: cppo.id,
          buyer: (
            <ResponsiveColumn
              linkText={cppo?.companyName || '-'}
              maxWidth={columnMaxWidth}
            />
          ),
          authorizationName: (
            <ResponsiveColumn
              linkText={cppo?.resellerAgreement?.agreementName || '-'}
              linkUrl={getLabraResaleAuthDetailPageUrl(
                cppo?.resaleAuthorizationId
              )}
              maxWidth={columnMaxWidth}
            />
          ),
          expiryDate: !isEmpty(cppo?.offerExpirationDate) ? (
            <ShowExpirationDateWithIcon
              date={cppo?.offerExpirationDate || ''}
              daysToCompare={5}
              daysLessLimit={15}
              expirePrefix="Ends in"
            />
          ) : (
            '-'
          ),
          type: getOfferTypeWithToolTip(cppo?.type || '-'),
          offerStatus: isEmpty(cppo?.offerStatus) ? (
            '-'
          ) : (
            <StatusChipV3 {...statusDetails} />
          ),
          offerId: <ShortenedNameV2 name={cppo?.offerId || '-'} length={23} />,
          actions: (
            <div
              className={commonClasses.actionColumn}
              data-prevent-row-click
              data-testid={'actions'}
            >
              <ColumnActionWithMenuV2
                handleSelectOption={handleSelectOption(cppo)}
                menuOptions={getCppoMenuOptions(
                  cppo,
                  'AWS',
                  baseUrl,
                  canWriteMarketplace,
                  copied
                )}
              />
            </div>
          ) as never,
        }
      }),
    ]
  }

  const handleSyncButtonClick = () => {
    dispatch(syncReceivedResaleAuthorizations(routeAccountId || ''))
    setDisplayWaitTime(true)
  }

  const handleClickRow = (selectedRowId: string) => {
    const selectedItem = awsCppos?.find(item => item?.id === selectedRowId)
    if (selectedItem) {
      const options = getCppoMenuOptions(
        selectedItem,
        'AWS',
        baseUrl,
        canWriteMarketplace,
        copied
      )
      options[0].route && navigate(options[0].route)
    }
  }

  const partnerUser = useUserType()
  const tenantPartnerId = useTenantPartnerId()
  const { partnerData } = usePartner(tenantPartnerId || '', partnerUser)
  const partnershipType = (
    partnerData as { partnershipType?: string | string[] } | null
  )?.partnershipType

  const onyIsv =
    !partnershipType?.includes('CONSULTING_PARTNER') &&
    partnershipType?.includes('TECHNOLOGY_PARTNER')

  if (onyIsv) {
    return <IsvChannelOffersEmptyState />
  }

  return (
    <>
      {renderSyncActiveResaleAuthEmptyState ? (
        <SyncResaleAuthEmptyState
          onSync={handleSyncButtonClick}
          isLoadingSync={isLoadingSync}
          shouldDisplayWaitTime={shouldDisplayWaitTime}
        />
      ) : renderCreateCPPOEmptyState ? (
        <CreateChannelOfferEmptyState />
      ) : (
        <div data-testid="cppo-list-table" className={classes.cppoRoot}>
          <ChannelOffersFilters />
          <TableComponentV2
            isLoading={loading}
            keyName={'offers.channelOffers'}
            objects={getCppoListObjectsForLabraUI()}
            mapping={mappingChannelOfferForLabraUI}
            count={count || 0}
            fetchMoreData={fetchMoreData}
            selectedItem={[]}
            onChangeCheckbox={() => void 0}
            showCheckbox={false}
            breakWorkHeader={false}
            onChangeRow={handleClickRow}
            customColumns={['actions']}
            stickyColumns={['actions']}
            customCellProps={[
              { style: { width: columnMaxWidth } },
              { style: { width: columnMaxWidth } },
            ]}
            tableHeight={'calc(100vh - 242px)'}
          />
        </div>
      )}
      <div>
        {selectedCppoDetail?.offerExpirationDate && (
          <ExtendOfferExpirationDateModal
            offerExpirationDate={selectedCppoDetail?.offerExpirationDate}
            offerId={selectedCppoDetail.id}
            planType={selectedCppoDetail?.productType as PlanType}
            offerSubscriptionEndDate={selectedCppoDetail?.subscriptionEndDate}
            offerStatus={selectedCppoDetail?.offerStatus}
            isDraft={selectedCppoDetail?.draft}
            agreementStartDate={selectedCppoDetail?.agreementStartDate}
            type={selectedCppoDetail?.type as OfferTypeRenew}
            openExtendExpiryDateModal={!!openExpiryDateModalCPPO}
            onAcceptModalAction={onAcceptModalAction}
            onDeclineModalAction={onDeclineModalAction}
            extendOfferType="CPPO"
            resaleAuthorizationId={selectedCppoDetail?.resaleAuthorizationId}
            partnerIdFromURL={routeAccountId}
          />
        )}
        <ModalConfirmV2
          title={'Delete this channel offer? '}
          description={
            <Typography>
              <p>
                You will not be able to recover this offer or any information
                inside it once this offer is deleted.
              </p>

              <p>Are you sure you want to delete this offer?</p>
            </Typography>
          }
          open={openDeleteModal}
          titleAcceptButton={'Delete'}
          onAccept={handleConfirmDeleteModal}
          acceptLoading={deleteLoading}
          onDecline={handleCloseDeleteModal}
          variant={ModalSizeVariants.small}
          icon={
            <FontAwesomeIcon
              icon={faTrash}
              style={{ width: '14px', height: '16px' }}
            />
          }
          hideCancelIcon={false}
          hideCancelButton={false}
        />
      </div>
    </>
  )
}
